import { useCallback, useState, useMemo } from 'react';
import styled from '@emotion/styled';
import { Button, Space, message, Modal } from 'antd';
import { useTranslate } from '@tolgee/react';
import ItemCard, { ItemCardData } from 'components/ItemCard';
import { SmileOutlined, FrownOutlined, LoadingOutlined } from '@ant-design/icons';
import { updateOfferStatus, UpdateOfferStatusBody, updateOfferStatusApi } from 'services/api/offer';
import { useOfferMsgUpdate } from 'hooks/business/im/useMsgToConversation';
import { useCurrentConversation } from 'hooks/business/conversation';
import CardMessage, { CardMessageProps } from './CardMessage';
import { get } from 'lodash-es';
import { formatNumber } from 'utils/numberFormat';

export interface OfferCardMessageData extends ItemCardData {
  link: string;
  unit: string;
  offer: Conversation.Offer;
}

export interface OfferCardMessageProps extends CardMessageProps {
  storeId: string;
  msgId: string;
  externalMsgId: string;
  className?: string;
  msgContent: Conversation.MsgOffer;
  dataSource: OfferCardMessageData;
  isBuyerMessage: boolean;
}

const Footer = styled.div`
  margin-top: 8px;
  font-size: 12px;
`;
const ViewDetail = styled.div`
  text-align: right;
  padding-top: 8px;
  border-top: 1px dashed var(--fett-border-default);

  .ant-btn {
    font-size: 12px;
  }
  .ant-space {
    margin-right: 15px;
  }
`;
const BtnJujue = styled(Button)`
  color: #f05b5b;
`;
const BtnJieshou = styled(Button)`
  color: #336fff;
`;
const SummaryLine = styled.div`
  display: flex;
  font-size: 12px;
  color: var(--fett-text-secondary);
`;
const Label = styled.div`
  display: inline-block;
  margin-right: 2px;
  &:after {
    content: ':';
  }
`;
const Price = styled.div``;

const FlexWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const OfferCardMessage = (props: OfferCardMessageProps) => {
  const { t } = useTranslate();

  const {
    storeId,
    msgId,
    externalMsgId,
    className,
    msgContent,
    dataSource,
    isBuyerMessage,
    ...oProps
  } = props;

  const { status, offerId } = msgContent;

  const {
    offer: { offerStatus: offerStatusData, productCount, currency, offerPrice },
  } = dataSource;

  const offerStatus = status
    ? {
        pending: 1,
        accepted: 2,
        rejected: 3,
      }[status] || offerStatusData
    : offerStatusData;

  const currentConversation = useCurrentConversation();

  const offerMsgUpdate = useOfferMsgUpdate();
  const [loading, setLoading] = useState(false);
  const [toStatus, setToStatus] = useState('');

  const offerReq = useCallback(
    (operation: UpdateOfferStatusBody['operation']) => {
      setToStatus(operation);
      setLoading(true);
      updateOfferStatus({
        storeId,
        messageId: externalMsgId,
        offerId,
        operation,
      })
        .then(() => {
          setLoading(false);
          offerMsgUpdate(msgId, operation);
        })
        .catch((e) => {
          setLoading(false);
          const msg = get(e, 'response.data.msg');

          if (msg.includes('add_to_cart_limitation')) {
            Modal.error({
              title: t('aide.提示'),
              content: <>{t('errors.add_to_cart_limitation')}</>,
            });
          } else {
            let content = t('api.apiErrorWithCode', {
              api: updateOfferStatusApi,
              msg,
            });
            if (e.message.includes('500')) {
              content = t('ViewAppCenter.议价操作500');
            } else if (e.message.includes('566')) {
              content = t('ViewAppCenter.议价操作566_copy');
            }
            message.error(content);
          }
        });
    },
    [storeId, externalMsgId, offerId, offerMsgUpdate, msgId, t],
  );

  const infoAfter = useMemo(() => {
    return offerPrice ? (
      <>
        <SummaryLine>
          <Label>{t('ViewsChat.数量')}</Label>
          <div>{productCount}</div>
        </SummaryLine>
        <SummaryLine>
          <Label>{t('ViewAppCenter.出价')}</Label>
          <div>
            <Price>
              {currency || ''} {formatNumber(offerPrice, '0,0.00')}
            </Price>
          </div>
        </SummaryLine>
      </>
    ) : null;
  }, [currency, offerPrice, productCount, t]);

  return (
    <CardMessage className={className} {...oProps}>
      <ItemCard
        dataSource={dataSource}
        infoAfter={infoAfter}
        platform={currentConversation?.channel}
        region={currentConversation?.region}
      />
      <Footer>
        <ViewDetail>
          {offerStatus === 1 ? (
            <FlexWrap>
              <div />
              <div>
                <BtnJujue
                  disabled={loading || !currentConversation?.controlStatus}
                  type="link"
                  size="small"
                  onClick={() => offerReq('rejected')}
                >
                  {t('ViewAppCenter.议价拒绝')}{' '}
                  {loading && toStatus === 'rejected' ? <LoadingOutlined /> : null}
                </BtnJujue>
                <BtnJieshou
                  disabled={loading || !currentConversation?.controlStatus}
                  type="link"
                  size="small"
                  onClick={() => offerReq('accepted')}
                >
                  {t('ViewAppCenter.议价接受')}{' '}
                  {loading && toStatus === 'accepted' ? <LoadingOutlined /> : null}
                </BtnJieshou>
              </div>
            </FlexWrap>
          ) : null}
          {offerStatus === 2 ? (
            <Space size={4} style={{ color: '#00BF69' }}>
              <SmileOutlined />
              {t('ViewAppCenter.出价已接受')}
            </Space>
          ) : null}
          {offerStatus === 3 ? (
            <Space size={4} style={{ color: '#F05B5B' }}>
              <FrownOutlined />
              {t('ViewAppCenter.出价已拒绝')}
            </Space>
          ) : null}
          {offerStatus === 4 ? (
            <Space size={4} style={{ color: 'var(--fett-text-tertiary)' }}>
              {t('ViewAppCenter.买家已取消出价')}
            </Space>
          ) : null}
        </ViewDetail>
      </Footer>
    </CardMessage>
  );
};

export default OfferCardMessage;
